/*
 * What Ctrl/Cmd+K finds of this module (UX §1.5): every conversation the reader is in, and every
 * person they could write to. Before this the switcher knew five pages, which is a switcher that
 * cannot switch — and once the rail is places only, it is also the way to a person.
 *
 * The provider answers from the model and never from the network, so its rows arrive in the same
 * frame as core's own; `Promise.resolve` is the slot's shape, not a round trip.
 *
 * Matching is the switcher's own `scoreTarget`, imported rather than reimplemented: a provider that
 * filtered by substring would quietly turn off the subsequence match the switcher documents ("dv"
 * finds "#dev") for exactly the rows people use it for.
 *
 * The one thing it cannot read from the model is a public channel this person has never been in, and
 * that is the row they most need: a channel somebody left was otherwise reachable only through
 * *Browse channels* (X-A4 N18.5, B3-Z OPEN 2). Those rows come from the cached public directory
 * (`conversation/publicDirectory.ts`), which asks the browse route at most once a minute and answers
 * from memory in between — so the provider's rows still arrive in the frame the query was typed in.
 * The row says the reader is not a member, and its address is the channel's own: `/c/<slug>` shows the
 * history with the *Join* banner over it (UX §8 #8).
 *
 * A row of a conversation also carries what is waiting in it, because a person pressing Ctrl+K is
 * usually going to what is unread (X-B1 A2). The count is the sidebar's badge and not a second
 * arithmetic: `badgeCountOf` with the same silencing rule, and "it names me" by the same test the
 * sidebar pill uses.
 */

import type { QuickSwitcherProvider } from "../../core/modules/registry.ts"
import { scoreTarget, type SwitchTarget } from "../../core/shell/index.ts"
import { client } from "../../core/store/store.ts"
import { publicChannels, refreshPublicDirectory } from "./conversation/publicDirectory.ts"
import { conversationAddress, conversationTitle } from "./conversation/title.ts"
import { t } from "./i18n/t.ts"
import { conversationPath, dmPath } from "./navigation.ts"
import { badgeCountOf, chatSlice, isDirect, isSilenced, sidebarOrder } from "./state/selectors.ts"
import type { ConversationView } from "./wire/views.ts"

/** How many of this module's rows one query offers; the switcher shows twenty of everything. */
const MAX_ROWS = 30

/**
 * Every conversation the reader is actually in, named the way its sidebar row is named and offered
 * in the order the sidebar shows them (`sidebarOrder`): starred first, then the channels, then the
 * direct conversations, muted last inside each group. The order matters even though the rows are
 * scored, because rows that score the same keep the order they were built in — `sort` is stable —
 * and with an empty box every row scores the same.
 *
 * Behind them come the conversations that order leaves out: a row somebody has hidden is absent from
 * the sidebar and must still be reachable by name, which is what the switcher is for.
 */
function conversationTargets(): SwitchTarget[] {
	const model = client.api.getState()
	const slice = chatSlice(model)
	const meId = model.me?.id ?? 0
	const inSidebar = sidebarOrder(model)
	const shown = new Set(inSidebar)
	const now = Date.now()
	const targets: SwitchTarget[] = []
	for (const id of [...inSidebar, ...model.conversationOrder.filter((id) => !shown.has(id))]) {
		if (slice.self[id]?.state !== "active") continue
		const meta = model.conversations[id]?.meta as ConversationView | undefined
		if (!meta) continue
		const direct = isDirect(meta.kind)
		const others = (meta.memberIds ?? []).filter((member) => member !== meId)
		// What is waiting behind the row, by the sidebar's own two rules (X-B1 A2, G-71): a silent
		// conversation claims its mentions alone, and a count "names me" when it holds a mention or
		// when it is a direct conversation, where every message is addressed to the reader.
		const counted = model.conversations[id]
		const badge = badgeCountOf(counted, isSilenced(slice.self[id], now))
		const mine = (counted?.mentions ?? 0) > 0 || (direct && (counted?.unread ?? 0) > 0 && badge > 0)
		targets.push({
			id: `chat.conv.${String(id)}`,
			// The place, as every other surface names it: "#allgemein" for a channel, the people for
			// a direct conversation (X-A3 V21).
			label: conversationAddress(meta, model.users, meId),
			// A direct conversation's address *is* a handle ("bea", "anna+bert"), so it is written
			// like one — and a person's row below then reads the same, which is what lets the two
			// be recognised as one destination. A channel carries its display name instead, which is
			// what keeps it findable by the word people call it out loud.
			detail: direct ? `@${meta.dmHandle ?? ""}` : conversationTitle(meta, model.users, meId),
			to: direct ? dmPath(meta.dmHandle ?? "") : conversationPath(meta.slug ?? ""),
			// A direct conversation with one other person is that person: the row draws them rather
			// than a line of text, and is then the same picture as their own row above it.
			...(direct && others.length === 1 ? { userId: others[0] } : {}),
			...(badge > 0 ? { count: badge } : {}),
			...(badge > 0 && mine ? { mention: true } : {}),
		})
	}
	return targets
}

/**
 * The public channels this person is not in. A channel they are in arrives through the model above and
 * is the row that survives the deduplication, so this list never doubles a conversation.
 */
function joinableTargets(): SwitchTarget[] {
	// The ask, made where a side effect may be made: the switcher calls its providers from an effect,
	// so a query is the right moment to find out whether the directory is still fresh. What this call
	// reads is whatever is held now; a newer answer is on the next keystroke.
	refreshPublicDirectory()
	return publicChannels()
		.filter((row) => !row.joined && row.conversation.slug !== undefined)
		.map((row) => ({
			id: `chat.joinable.${String(row.conversation.id)}`,
			label: `#${row.conversation.slug ?? ""}`,
			// The name keeps the channel findable by the word people call it out loud, and the second
			// half says why the row will not simply open: the reader is not in it yet.
			detail: t("chat.switcher.joinable", {
				name: row.conversation.name ?? row.conversation.slug ?? "",
			}),
			to: conversationPath(row.conversation.slug ?? ""),
		}))
}

/**
 * Everybody but the reader. The address is `/d/<handle>`, which the route resolves to the existing
 * direct conversation or opens one — so a person is reachable whether or not they have ever been
 * written to, which is the whole point of finding them here.
 */
function peopleTargets(): SwitchTarget[] {
	const model = client.api.getState()
	const meId = model.me?.id ?? 0
	const targets: SwitchTarget[] = []
	for (const user of Object.values(model.users)) {
		if (!user || user.id === meId) continue
		// A bot has no session and never answers, so it is not a place to write to (X-A1 D14).
		if (user.kind === "bot") continue
		targets.push({
			id: `chat.person.${String(user.id)}`,
			label: user.displayName,
			// The row is about an account, so the switcher draws the person rather than their name.
			userId: user.id,
			// A deactivated account is still found — a person searching for a colleague who has left
			// deserves an answer — and the row says why writing to them will not work, instead of
			// leaving the reader pressing Enter on a row that does nothing. Only that one state earns
			// the sentence: an account nobody has signed in to yet reads `invited`, and writing to
			// them works perfectly well — the message waits for them.
			detail:
				user.state === "deactivated"
					? t("chat.switcher.deactivated", { handle: user.handle })
					: `@${user.handle}`,
			to: dmPath(user.handle),
		})
	}
	return targets
}

/**
 * Whether any word of a row begins with what was typed. It is the match a person means when they type
 * three letters: "dan" means Dana, and only if nobody is called that does the subsequence match get to
 * answer with "Admin Adler" through its `@admin` (D-10 P15).
 *
 * A word begins at the start of the text, after a space, or after one of the characters this product
 * starts a name with: the hash of a channel, the at of a handle, and the dash and plus a slug and a
 * group handle are written with.
 */
function startsAWord(term: string, text: string): boolean {
	const needle = term.toLowerCase()
	const hay = text.toLowerCase()
	for (let at = hay.indexOf(needle); at >= 0; at = hay.indexOf(needle, at + 1)) {
		if (at === 0 || /[\s#@+-]/.test(hay.charAt(at - 1))) return true
	}
	return false
}

export const chatQuickSwitcher: QuickSwitcherProvider = {
	id: "chat",
	query(term: string): Promise<SwitchTarget[]> {
		// A person one already has a direct conversation with is the same destination as that
		// conversation — `/d/<handle>` for both — so the address is what identity means here, and the
		// conversation, which is listed first, is the row that survives. Two rows that open the same
		// screen are two chances to pick the wrong one.
		const byAddress = new Map<string, SwitchTarget>()
		for (const target of [...conversationTargets(), ...peopleTargets(), ...joinableTargets()]) {
			if (!byAddress.has(target.to)) byAddress.set(target.to, target)
		}
		const scored: { target: SwitchTarget; score: number }[] = []
		const prefixed: { target: SwitchTarget; score: number }[] = []
		for (const target of byAddress.values()) {
			const text = `${target.label} ${target.detail ?? ""}`
			const score = scoreTarget(term, text)
			if (score === null) continue
			scored.push({ target, score })
			if (startsAWord(term, text)) prefixed.push({ target, score })
		}
		// The fuzzy rows are the answer only when nothing begins with what was typed: a list that mixed
		// the two put "Admin Adler" above "Dana" for "dan", which is a list a person has to read twice.
		const answer = term.trim() !== "" && prefixed.length > 0 ? prefixed : scored
		answer.sort((a, b) => b.score - a.score)
		return Promise.resolve(answer.slice(0, MAX_ROWS).map((row) => row.target))
	},
}
